import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import userStore from "../../zustand/userStore";
import {
  GetPaymentPlanDetailsThunk,
  InitiatePaymentThunk,
} from "./redux/thunks";
import { logout } from "../../auth/authSlice";
import Footer from "../../components/Footer";
// import OfferTimerBanner from "../../components/OfferTimerBanner";
import SkeletonLoader from "../QuestionPaper/createpaper/components/loader/skeletonLoader";
import "./pricingpage.css";
import { setAppdAiGuest, setAppdAiUser } from "../../utils/appdAiHelpers";
import { trackMetaPixelRegistration } from "../../utils/metaPixel";
import CSRSchoolPlanForm from "../../components/CSRForm";
import { getStatusAPI } from "../ticketing-app/redux/ticketingSlice";

// School plan modules display (Home.js-style cards)
const schoolModulesList = [
  {
    name: "Create Question Paper",
    subName: "प्रश्न पत्र बनाएं",
    color: "#da5252",
    icon: "/images/questionpaper.png",
    dynamic: true,
    badgeVariant: "papers",
  },
  {
    name: "Create Academic Calendar",
    subName: "अकादमिक कैलेंडर बनाएं",
    color: "#d80564",
    icon: "/images/3.png",
  },
  {
    name: "Create Time Table",
    subName: "समय सारिणी बनाएं",
    color: "#16CCA3",
    icon: "/images/timetable.png",
  },
  {
    name: "Create Event Organiser",
    subName: "इवेंट ऑर्गेनाइजर बनाएं",
    color: "#86bc28",
    icon: "/images/event.png",
  },
  {
    name: "Create Teachers Planner",
    subName: "टीचर्स प्लानर बनाएं",
    color: "#d4166b",
    icon: "/images/teacherplanner.png",
  },
  // { name: "Documents & Correspondences", subName: "दस्तावेज़ और पत्राचार", color: "#018bd6", icon: "/images/documents.png", badge: "FREE", badgeVariant: "free" },
  {
    name: "Olympiads & Quiz",
    subName: "क्विज़ बनाएं",
    color: "#f89406",
    icon: "/images/quizicon.png",
    badge: "COMING SOON",
    badgeVariant: "soon",
  },
  // { name: "Create User Management", color: "#9b1616", icon: "/images/adduser.png" },
];

// Question paper count per user plan size
// const qpCountByUsers = { 10: 400, 25: 1000, 40: 1600, 60: 2400 };
const qpCountByUsers = {
  25: 1000,
  50: 2000,
  100: 4000,
};

const schoolPlanThemes = {
  25: {
    gradient: "linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)",
    selectedGradient: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)",
    borderColor: "#1d4ed8",
    shadow: "0 10px 24px rgba(37, 99, 235, 0.22)",
    selectedShadow: "0 14px 30px rgba(29, 78, 216, 0.32)",
  },
  50: {
    gradient: "linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)",
    selectedGradient: "linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)",
    borderColor: "#6d28d9",
    shadow: "0 10px 24px rgba(124, 58, 237, 0.22)",
    selectedShadow: "0 14px 30px rgba(109, 40, 217, 0.32)",
  },
  100: {
    gradient: "linear-gradient(135deg, #db2777 0%, #fb7185 100%)",
    selectedGradient: "linear-gradient(135deg, #be185d 0%, #f43f5e 100%)",
    borderColor: "#be185d",
    shadow: "0 10px 24px rgba(219, 39, 119, 0.22)",
    selectedShadow: "0 14px 30px rgba(190, 24, 93, 0.32)",
  },
  120: {
    gradient: "linear-gradient(135deg, #ea580c 0%, #f59e0b 100%)",
    selectedGradient: "linear-gradient(135deg, #c2410c 0%, #ea580c 100%)",
    borderColor: "#c2410c",
    shadow: "0 10px 24px rgba(234, 88, 12, 0.22)",
    selectedShadow: "0 14px 30px rgba(194, 65, 12, 0.32)",
  },
};

const defaultSchoolPlanTheme = {
  gradient: "linear-gradient(135deg, #0b3a83 0%, #3a8fd9 100%)",
  selectedGradient: "linear-gradient(135deg, #082c66 0%, #2563eb 100%)",
  borderColor: "#0b3a83",
  shadow: "0 10px 24px rgba(11, 58, 131, 0.2)",
  selectedShadow: "0 14px 30px rgba(11, 58, 131, 0.3)",
};

// Available modules for individual plan selection (landing page card style)
const allModules = [
  {
    id: 2,
    name: "Create Question Papers",
    subName: "प्रश्न पत्र बनाएं",
    gradient: "linear-gradient(135deg, #C34956 0%, #E56B77 100%)",
    icon: "/images/questionpaper.png",
    badge: "50 PAPERS",
    badgeVariant: "papers",
  },
  {
    id: 3,
    name: "Create Timetable",
    subName: "समय सारिणी बनाएं",
    gradient: "linear-gradient(135deg, #16CCA3 0%, #30d3ad 100%)",
    icon: "/images/timetable.png",
    badge: "UNLIMITED",
    badgeVariant: "unlimited",
  },
  {
    id: 4,
    name: "Create Academic Calendar",
    subName: "अकादमिक कैलेंडर बनाएं",
    gradient: "linear-gradient(135deg, #d80564 0%, #f53587 100%)",
    icon: "/images/3.png",
    badge: "UNLIMITED",
    badgeVariant: "unlimited",
  },
  {
    id: 1,
    name: "Create Event Organiser",
    subName: "इवेंट ऑर्गेनाइजर बनाएं",
    gradient: "linear-gradient(135deg, #86bc28 0%, #a2d93a 100%)",
    icon: "/images/event.png",
    badge: "UNLIMITED",
    badgeVariant: "unlimited",
  },
  // { id: 5, name: "Teachers Planner", subName: "टीचर्स प्लानर", gradient: "linear-gradient(135deg, #d4166b 0%, #f03090 100%)", icon: "/images/teacherplanner.png", badge: "UNLIMITED", badgeVariant: "unlimited", requiresModule: 3 },
  // { id: 6, name: "Documents & Correspondences", subName: "दस्तावेज़ और पत्राचार", gradient: "linear-gradient(135deg, #018bd6 0%, #33aaee 100%)", icon: "/images/documents.png", badge: "Free with every feature", badgeVariant: "free", locked: true },
];

function PricingPage() {
  const [showPopUp, setShowPopUp] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((store) => store.authSlice);
  const userData = userStore((state) => state.user);

  // Get plan details from Redux (fetched from API)
  const { loader, error, planDetails } = useSelector(
    (store) => store.paymentGateway,
  );

  useEffect(() => {
    if (userData) setAppdAiUser(userData, "pricing");
    else setAppdAiGuest("pricing");
  }, []);
  // Fetch plans from API on mount
  useEffect(() => {
    dispatch(GetPaymentPlanDetailsThunk());
  }, [dispatch]);

  // Add body class so pricingpage.css rules are scoped to this page only
  useEffect(() => {
    document.body.classList.add("on-pricing-page");
    return () => document.body.classList.remove("on-pricing-page");
  }, []);

  // Fire Meta Pixel when redirected back with ?payment-status=1 or 0
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get("payment-status");
    if (paymentStatus === "1" || paymentStatus === "0") {
      trackMetaPixelRegistration();
    }
  }, [location.search]);

  // Block browser back navigation while on the pricing page (works for every back press)
  useEffect(() => {
    // Push two trap entries so a forward jump always has somewhere to land
    window.history.pushState(null, "", window.location.href);
    window.history.pushState(null, "", window.location.href);

    const onPopState = () => {
      // Walk forward to the trap entry, then top it up so the next back has one to consume
      window.history.go(1);
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Separate complete and customized plans from API
  const completePlans =
    planDetails?.filter((p) => p.model_type === "complete") || [];
  const customizedPlans =
    planDetails?.filter((p) => p.model_type === "customized") || [];
  // Special offer plan from API (used by exclusive deal banner + popup)
  const specialPlan =
    planDetails?.find((p) => p.model_type === "special") || null;
  // All available feature IDs (for auto-selecting in the special offer popup)
  const allFeatureIds = [...allModules.map((m) => m.id), 6];
  const specialPrice = Number(specialPlan?.price ?? 0);

  // Complete Plan State
  const [selectedCompletePlan, setSelectedCompletePlan] = useState(null);
  const [completeCouponCode, setCompleteCouponCode] = useState("");
  const [completeAppliedCoupon, setCompleteAppliedCoupon] = useState(null);
  const [completeCouponId, setCompleteCouponId] = useState(null);

  // Customized Plan State
  // Documents (id:6) always pre-selected
  const [selectedModules, setSelectedModules] = useState([6]);
  const [numberOfUsers, setNumberOfUsers] = useState(1);
  const [customCouponCode, setCustomCouponCode] = useState("");
  const [customAppliedCoupon, setCustomAppliedCoupon] = useState(null);
  const [customCouponId, setCustomCouponId] = useState(null);

  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [mobileTab, setMobileTab] = useState("individual");

  // Special-offer state
  const [openModal, setOpenModal] = useState(null);
  const [dealTimer, setDealTimer] = useState({ hrs: 0, mins: 0, secs: 0 });
  const [isDealEligible, setIsDealEligible] = useState(false);
  const [showHomeIcon, setShowHomeIcon] = useState(false);

  // Shown while a payment is being initiated (page will redirect on success)
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Second special-offer card grants a flat 35% off on Individual + School (main) plans
  const DEAL_DISCOUNT_PCT = 35;
  const [dealDiscountApplied, setDealDiscountApplied] = useState(false);

  // Skip Payment bar: visible only within 15 minutes of registration (same pattern as OfferTimerBanner)
  const [skipPaymentExpired, setSkipPaymentExpired] = useState(
    !userData?.created,
  );
  useEffect(() => {
    if (!userData?.created) {
      setSkipPaymentExpired(true);
      return;
    }
    const deadline = new Date(userData.created).getTime() + 15 * 60 * 1000;
    const tick = () => {
      if (Date.now() >= deadline) {
        setSkipPaymentExpired(true);
        return true;
      }
      setSkipPaymentExpired(false);
      return false;
    };
    if (tick()) return;
    const id = setInterval(() => {
      if (tick()) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [userData?.created]);

  // Special offer: delay-show the home icon
  useEffect(() => {
    const t = setTimeout(() => setShowHomeIcon(true), 10000);
    return () => clearTimeout(t);
  }, []);

  // Special offer: countdown timer (eligible within 89 minutes of registration)
  useEffect(() => {
    if (!userData?.created) {
      setIsDealEligible(false);
      setDealTimer({ hrs: 0, mins: 0, secs: 0 });
      return;
    }
    const createdAt = new Date(userData.created).getTime();
    const TOTAL_MS = 60 * 60 * 1000;

    const tick = () => {
      const remainingMs = createdAt + TOTAL_MS - Date.now();
      if (remainingMs <= 0) {
        setIsDealEligible(false);
        setDealTimer({ hrs: 0, mins: 0, secs: 0 });
        return false;
      }
      setIsDealEligible(true);
      setDealTimer({
        hrs: Math.floor(remainingMs / 3600000),
        mins: Math.floor((remainingMs % 3600000) / 60000),
        secs: Math.floor((remainingMs % 60000) / 1000),
      });
      return true;
    };

    if (!tick()) return;
    const interval = setInterval(() => {
      if (!tick()) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [userData]);

  const padTime = (n) => String(n).padStart(2, "0");

  // Special offer: lock body scroll while popup open
  useEffect(() => {
    if (openModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [openModal]);

  // Auto-fill coupon code if passed from paywall (only within 15 minutes of registration)
  useEffect(() => {
    const couponCode = location.state?.couponCode;
    if (couponCode && userData?.created) {
      const createdAt = new Date(userData.created);
      const now = new Date();
      const minutesSinceCreation = (now - createdAt) / (1000 * 60);
      if (minutesSinceCreation <= 15) {
        setCompleteCouponCode(couponCode);
        setCustomCouponCode(couponCode);
      }
    }
  }, [location.state, userData]);

  const logOut = () => {
    dispatch(logout());
    navigate("/home");
  };

  // Auto-determine plan from number of selected non-document modules
  // documents+1=id9, +2=id10, +3=id11, +4=id12, +5=id13
  const selectedCustomPlan = useMemo(() => {
    const nonDocCount = selectedModules.filter((id) => id !== 6).length;
    if (nonDocCount === 0) return null;
    const targetModelId = 8 + nonDocCount;
    return customizedPlans.find((p) => p.model_id === targetModelId) || null;
  }, [selectedModules, customizedPlans]);

  // Tier pricing: ₹250 per module, no multi-module discount
  const CUSTOM_TIER = {
    1: { base: 250, discountPct: 0, final: 250 },
    2: { base: 500, discountPct: 0, final: 500 },
    3: { base: 750, discountPct: 0, final: 750 },
    4: { base: 1000, discountPct: 0, final: 1000 },
  };

  const customTier = useMemo(() => {
    const nonDocCount = selectedModules.filter((id) => id !== 6).length;
    return CUSTOM_TIER[nonDocCount] || null;
  }, [selectedModules]);

  // Base price per user (before discount), for breakdown display
  const customizedBasePricePerUser = customTier?.base ?? 0;
  // Discounted price per user
  const customizedPricePerUser = customTier?.final ?? 0;
  // Total after tier discount × users
  const customizedPrice = customizedPricePerUser * numberOfUsers;
  // Apply the 35% special-deal discount if the user clicked the second offer card
  const customizedPriceAfterDiscount = dealDiscountApplied
    ? Math.round(customizedPrice * (1 - DEAL_DISCOUNT_PCT / 100))
    : customizedPrice;

  // Calculate final price with coupon for complete plan (35% deal discount applied first if active)
  const completePlanFinalPrice = useMemo(() => {
    if (!selectedCompletePlan) return 0;
    const rawBase = Number(selectedCompletePlan.price);
    const basePrice = dealDiscountApplied
      ? Math.round(rawBase * (1 - DEAL_DISCOUNT_PCT / 100))
      : rawBase;
    if (!completeAppliedCoupon) return basePrice;
    const { discount } = completeAppliedCoupon;
    if (typeof discount === "string" && discount.includes("%")) {
      const percentage = parseFloat(discount.replace("%", ""));
      return Math.round(basePrice - (basePrice * percentage) / 100);
    }
    return Math.max(0, basePrice - parseFloat(discount));
  }, [selectedCompletePlan, completeAppliedCoupon, dealDiscountApplied]);

  // Final price after coupon (tier discount already baked in)
  const customizedFinalPrice = useMemo(() => {
    if (!customAppliedCoupon) return customizedPriceAfterDiscount;
    const { discount } = customAppliedCoupon;
    if (typeof discount === "string" && discount.includes("%")) {
      const percentage = parseFloat(discount.replace("%", ""));
      return Math.round(
        customizedPriceAfterDiscount -
        (customizedPriceAfterDiscount * percentage) / 100,
      );
    }
    return Math.max(0, customizedPriceAfterDiscount - parseFloat(discount));
  }, [customizedPriceAfterDiscount, customAppliedCoupon]);

  // Valid when at least 1 non-document module is selected
  const isCustomizedValid = useMemo(() => {
    return selectedCustomPlan !== null && numberOfUsers >= 1;
  }, [selectedCustomPlan, numberOfUsers]);

  // Handle module card toggle
  const handleModuleToggle = (moduleId) => {
    if (moduleId === 6) return; // Documents always locked-on

    if (selectedModules.includes(moduleId)) {
      let newSelection = selectedModules.filter((id) => id !== moduleId);
      // If removing Time Table, also remove Teachers Planner
      if (moduleId === 3 && selectedModules.includes(5)) {
        newSelection = newSelection.filter((id) => id !== 5);
      }
      setSelectedModules(newSelection);
    } else {
      let newSelection = [...selectedModules, moduleId];
      // If adding Teachers Planner, auto-add Time Table if missing
      if (moduleId === 5 && !selectedModules.includes(3)) {
        newSelection = [...newSelection, 3];
      }
      setSelectedModules(newSelection);
    }
  };

  // Special offer popup helpers — price is fixed (flat amount from API)
  const popupHasDeal = !!specialPlan;

  const resetPopupSelection = () => {
    setSelectedModules([6]);
  };

  const openIndividualPopup = () => {
    // Special offer: auto-select all features
    setSelectedModules(allFeatureIds);
    setOpenModal("individual");
  };

  const closePopupAndReset = () => {
    resetPopupSelection();
    setOpenModal(null);
  };

  const handlePopupModuleToggle = (moduleId) => {
    // Special offer: all features are pre-selected and locked. No toggling.
    return;
  };

  const selectedUsers = selectedCompletePlan?.total_users;
  const paperCount = qpCountByUsers[selectedUsers] || 1000;

  // Coupon handlers for Complete Plan
  const handleCompleteApplyCoupon = async () => {
    if (!completeCouponCode.trim()) return alert("Please enter a coupon code.");
    if (!selectedCompletePlan) return alert("Please select a plan first.");
    if (completeCouponCode.trim().toUpperCase() === "WELCOME20") {
      if (!userData?.created) {
        return alert("Your coupon code has expired.");
      }
      const minutesSinceCreation =
        (new Date() - new Date(userData.created)) / (1000 * 60);
      if (minutesSinceCreation > 15) {
        return alert(
          "Your coupon code has expired. It was valid only within 15 minutes of registration.",
        );
      }
    }
    setIsValidatingCoupon(true);
    try {
      const res = await fetch(
        "https://web1024.docmaster.in:5010/api/coupons/validate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
          body: JSON.stringify({
            code: completeCouponCode,
            plan_types: [String(selectedCompletePlan.model_id)],
            school_id: userData?.admin_id,
          }),
        },
      );
      const data = await res.json();
      if (!data.success) return alert(data.message || "Invalid coupon");
      setCompleteCouponId(data.id);
      setCompleteAppliedCoupon({
        code: completeCouponCode,
        discount: data.discount || 0,
      });
    } catch (error) {
      alert("Error validating coupon");
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleCompleteRemoveCoupon = () => {
    setCompleteCouponCode("");
    setCompleteAppliedCoupon(null);
    setCompleteCouponId(null);
  };

  // Coupon handlers for Customized Plan
  const handleCustomApplyCoupon = async () => {
    if (!customCouponCode.trim()) return alert("Please enter a coupon code.");
    if (!selectedCustomPlan) return alert("Please select a plan first.");
    if (customCouponCode.trim().toUpperCase() === "WELCOME20") {
      if (!userData?.created) {
        return alert("Your coupon code has expired.");
      }
      const minutesSinceCreation =
        (new Date() - new Date(userData.created)) / (1000 * 60);
      if (minutesSinceCreation > 15) {
        return alert(
          "Your coupon code has expired. It was valid only within 15 minutes of registration.",
        );
      }
    }
    setIsValidatingCoupon(true);
    try {
      const res = await fetch(
        "https://web1024.docmaster.in:5010/api/coupons/validate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
          body: JSON.stringify({
            code: customCouponCode,
            plan_types: [String(selectedCustomPlan.model_id)],
            school_id: userData?.admin_id,
          }),
        },
      );
      const data = await res.json();
      if (!data.success) return alert(data.message || "Invalid coupon");
      setCustomCouponId(data.id);
      setCustomAppliedCoupon({
        code: customCouponCode,
        discount: data.discount || 0,
      });
    } catch (error) {
      alert("Error validating coupon");
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleCustomRemoveCoupon = () => {
    setCustomCouponCode("");
    setCustomAppliedCoupon(null);
    setCustomCouponId(null);
  };

  // Create coupon link
  const createCouponLink = async (couponId) => {
    if (!couponId) return null;
    const schoolId = userData?.admin_id;
    try {
      const response = await fetch(
        "https://web1024.docmaster.in:5010/api/coupons/createCouponLink",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
          body: JSON.stringify({
            schoolId,
            couponId,
            createdById: schoolId,
            status: "incompleted",
          }),
        },
      );
      if (response.ok) {
        const data = await response.json();
        return data.insertId;
      }
      return null;
    } catch (error) {
      console.error("Error creating coupon link:", error);
      return null;
    }
  };

  // Payment handler for Complete Plan
  const handleCompletePayment = async () => {
    if (!user) return alert("Please login to continue.");
    if (!selectedCompletePlan) return alert("Please select a plan.");

    const linkData = await createCouponLink(completeCouponId);

    // const body = {
    //   txnRefNo: Date.now(),
    //   amount: completePlanFinalPrice,
    //   txnType: "Pay",
    //   orderInfo: String(selectedCompletePlan.model_id),
    //   email: user.email,
    //   firstName: "sample",
    //   lastName: "sample",
    //   street: "Delhi",
    //   city: "",
    //   state: "",
    //   zip: 506002,
    //   phone: user?.contact_number || 0,
    //   UDF01: user.admin_id,
    //   UDF02: user?.id,
    //   UDF03: [],
    //   UDF04: "complete",
    //   UDF05: 1,
    //   UDF06: linkData || "",
    // };

    const body = {
      txnRefNo: Date.now(),
      amount: completePlanFinalPrice,
      // amount: 1,
      txnType: "Pay",
      orderInfo: String(selectedCompletePlan.model_id),
      email: user.email,
      firstName: user.admin_name || "",
      lastName: "",
      street: "Delhi",
      city: "",
      state: "",
      zip: 506002,
      phone: user?.contact_number || 0,
      udf21: user.admin_id,
      udf22: user?.id,
      udf23: [],
      udf24: "complete",
      udf25: 1,
      udf26: linkData || "",
      udf27: String(selectedCompletePlan.model_id),
    };
    console.log("Complete Plan Payment Body:", body);
    dispatch(InitiatePaymentThunk(body));
  };

  // Payment handler for Customized Plan
  const handleCustomizedPayment = async () => {
    if (!user) return alert("Please login to continue.");
    if (!isCustomizedValid) return alert("Please complete your selection.");

    // Check if Teachers Planner is selected without Time Table
    if (selectedModules.includes(5) && !selectedModules.includes(3)) {
      return alert("Please select Time Table to use Teacher's Planner.");
    }

    const linkData = await createCouponLink(customCouponId);

    // const body = {
    //   txnRefNo: Date.now(),
    //   amount: customizedFinalPrice,
    //   txnType: "Pay",
    //   orderInfo: String(selectedCustomPlan.model_id),
    //   email: user.email,
    //   firstName: "sample",
    //   lastName: "sample",
    //   street: "Delhi",
    //   city: "",
    //   state: "",
    //   zip: 506002,
    //   phone: user?.contact_number || 0,
    //   UDF01: user.admin_id,
    //   UDF02: user?.id,
    //   UDF03: selectedModules.join(","),
    //   UDF04: "customized",
    //   UDF05: numberOfUsers,
    //   UDF06: linkData || "",
    // };

    const body = {
      txnRefNo: Date.now(),
      amount: customizedFinalPrice,
      // amount: 1,
      txnType: "Pay",
      orderInfo: String(selectedCustomPlan.model_id),
      email: user.email,
      firstName: user.admin_name || "",
      lastName: "",
      street: "Delhi",
      city: "",
      state: "",
      zip: 506002,
      phone: user?.contact_number || 0,
      udf21: user.admin_id,
      udf22: user?.id,
      udf23: selectedModules.join(","),
      udf24: "customized",
      udf25: numberOfUsers,
      udf26: linkData || "",
      udf27: String(selectedCustomPlan.model_id),
    };
    console.log("Customized Plan Payment Body:", body);
    dispatch(InitiatePaymentThunk(body));
  };

  // Special offer: school deal — direct payment for the 10-user complete plan
  const handleSchoolDealPayment = async () => {
    if (!user) return alert("Please login to continue.");

    const tenUserPlan = completePlans.find((p) => p.total_users === 10);
    if (!tenUserPlan) return alert("This deal is not available right now.");

    const SCHOOL_DEAL_PRICE = 3499;

    const body = {
      txnRefNo: Date.now(),
      amount: SCHOOL_DEAL_PRICE,
      txnType: "Pay",
      orderInfo: String(tenUserPlan.model_id),
      email: user.email,
      firstName: user.admin_name || "",
      lastName: "",
      street: "Delhi",
      city: "",
      state: "",
      zip: 506002,
      phone: user?.contact_number || 0,
      udf21: user.admin_id,
      udf22: user?.id,
      udf23: [],
      udf24: "complete",
      udf25: 1,
      udf26: "",
      udf27: String(tenUserPlan.model_id),
    };
    console.log("School Deal Payment Body:", body);
    setPaymentLoading(true);
    dispatch(InitiatePaymentThunk(body));
  };

  // Special offer: individual popup deal — uses model_type === "special" plan from API
  const handlePopupPayment = async () => {
    if (!user) return alert("Please login to continue.");
    if (!specialPlan) return alert("This deal is not available right now.");

    const body = {
      txnRefNo: Date.now(),
      amount: Number(specialPlan.price),
      txnType: "Pay",
      orderInfo: String(specialPlan.model_id),
      email: user.email,
      firstName: user.admin_name || "",
      lastName: "",
      street: "Delhi",
      city: "",
      state: "",
      zip: 506002,
      phone: user?.contact_number || 0,
      udf21: user.admin_id,
      udf22: user?.id,
      udf23: allFeatureIds.join(","),
      udf24: "special",
      udf25: 1,
      udf26: "",
      udf27: String(specialPlan.model_id),
    };
    setPaymentLoading(true);
    dispatch(InitiatePaymentThunk(body));
  };

  // Show loader while fetching plans
  if (loader) return <SkeletonLoader type={1} />;
  if (error) {
    alert(error);
    return null;
  }

  return (
    <div className="pricing-page-wrapper">
      {/* Navbar */}
      <nav
        className="navbar navbar-expand-lg p-0"
        style={{
          background: "linear-gradient(135deg, #0b3a83, #1e5799, #3a8fd9)",
          fontSize: "0.85rem",
          boxShadow: "0 2px 12px rgba(11, 58, 131, 0.3)",
        }}
      >
        <div
          className="container-fluid px-0 mx-auto"
          style={{ maxWidth: "100%", position: "relative" }}
        >
          {/* Home icon - visible on mobile instead of hamburger */}
          <Link
            to="/dashboard"
            className="nav-link text-white d-lg-none ms-1"
            style={{ fontSize: "1rem" }}
          >
            <i className="fa fa-home"></i>
          </Link>

          {/* Desktop contact numbers — centered in navbar */}
          <div
            className="d-none d-lg-flex align-items-center gap-3"
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <a
              href="https://wa.me/919289440049?text=Hi%2C%20I%20want%20to%20know%20more%20about%20DocMaster%20services."
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "#ffffff",
                fontWeight: "600",
                fontSize: "16px",
                textDecoration: "none",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="#25D366"
                viewBox="0 0 24 24"
              >
                <path d="M20.52 3.48A11.8 11.8 0 0 0 12.05 0C5.5 0 .19 5.31.19 11.86c0 2.09.55 4.13 1.6 5.92L0 24l6.39-1.67a11.83 11.83 0 0 0 5.66 1.45h.01c6.55 0 11.86-5.31 11.86-11.86 0-3.17-1.23-6.15-3.4-8.44zM12.06 21.7a9.8 9.8 0 0 1-5-1.38l-.36-.21-3.79 1 1.01-3.7-.23-.38a9.8 9.8 0 1 1 8.37 4.67zm5.44-7.35c-.3-.15-1.78-.88-2.06-.98-.28-.1-.48-.15-.68.15-.2.3-.78.98-.96 1.18-.18.2-.35.23-.65.08-.3-.15-1.28-.47-2.43-1.5-.9-.8-1.5-1.78-1.68-2.08-.18-.3-.02-.46.13-.6.14-.14.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.03-.53-.08-.15-.68-1.63-.94-2.24-.25-.6-.5-.52-.68-.53h-.58c-.2 0-.53.08-.8.38-.28.3-1.05 1.03-1.05 2.5 0 1.48 1.08 2.9 1.23 3.1.15.2 2.12 3.24 5.14 4.54.72.31 1.28.5 1.72.64.72.23 1.38.2 1.9.12.58-.09 1.78-.73 2.03-1.44.25-.7.25-1.3.18-1.44-.08-.15-.28-.23-.58-.38z" />
              </svg>
              9289 44 00 49
            </a>
            <span style={{ color: "rgba(255,255,255,0.5)" }}>|</span>
            <a
              href="tel:+919289440049"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                color: "#ffffff",
                fontWeight: "600",
                fontSize: "16px",
                textDecoration: "none",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                fill="#ffffff"
                viewBox="0 0 24 24"
              >
                <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" />
              </svg>
              9289 44 00 49
            </a>
          </div>

          {/* Mobile contact numbers — only visible on mobile */}
          <div className="pricing-mobile-contacts d-lg-none">
            {/* <a
              href="https://wa.me/919289440049?text=Hi%2C%20I%20want%20to%20know%20more%20about%20DocMaster%20services."
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-contact-link"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="#25D366" viewBox="0 0 24 24">
                <path d="M20.52 3.48A11.8 11.8 0 0 0 12.05 0C5.5 0 .19 5.31.19 11.86c0 2.09.55 4.13 1.6 5.92L0 24l6.39-1.67a11.83 11.83 0 0 0 5.66 1.45h.01c6.55 0 11.86-5.31 11.86-11.86 0-3.17-1.23-6.15-3.4-8.44zM12.06 21.7a9.8 9.8 0 0 1-5-1.38l-.36-.21-3.79 1 1.01-3.7-.23-.38a9.8 9.8 0 1 1 8.37 4.67zm5.44-7.35c-.3-.15-1.78-.88-2.06-.98-.28-.1-.48-.15-.68.15-.2.3-.78.98-.96 1.18-.18.2-.35.23-.65.08-.3-.15-1.28-.47-2.43-1.5-.9-.8-1.5-1.78-1.68-2.08-.18-.3-.02-.46.13-.6.14-.14.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.03-.53-.08-.15-.68-1.63-.94-2.24-.25-.6-.5-.52-.68-.53h-.58c-.2 0-.53.08-.8.38-.28.3-1.05 1.03-1.05 2.5 0 1.48 1.08 2.9 1.23 3.1.15.2 2.12 3.24 5.14 4.54.72.31 1.28.5 1.72.64.72.23 1.38.2 1.9.12.58-.09 1.78-.73 2.03-1.44.25-.7.25-1.3.18-1.44-.08-.15-.28-.23-.58-.38z" />
              </svg>
              <span className="contact-number-text">9289 44 00 49</span>
            </a>
            <span className="pricing-contact-divider">|</span>
            <a href="tel:+919289440049" className="pricing-contact-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="#ffffff" viewBox="0 0 24 24">
                <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" />
              </svg>
              <span className="contact-number-text">9289 44 00 49</span>
            </a> */}
          </div>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ps-4 w-100 d-flex gap-3 justify-content-start">
              <li
                className={`nav-item ${["/dashboard"].includes(pathname) ? "active" : ""}`}
              >
                <Link to="/dashboard" className="nav-link text-white">
                  <i
                    className="fa fa-home"
                    style={{ pointerEvents: "none" }}
                  ></i>{" "}
                  Home
                </Link>
              </li>
            </ul>
          </div>
          {/* <div className="d-flex align-items-center justify-content-end gap-3">
            <div className="dropdown me-3">
              <button
                className="btn btn-light dropdown-toggle d-flex align-items-center pricing-profile-btn"
                type="button"
                id="userDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ borderRadius: "25px", fontWeight: "600" }}
              >
                <i className="fa fa-user me-2 me-lg-2"></i>
                <span className="d-none d-lg-inline">{userData.name}</span>
              </button>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="userDropdown"
              >
                <li>
                  <span className="dropdown-item">Profile</span>
                </li>
                <li>
                  <span className="dropdown-item" onClick={logOut}>
                    Log Out
                  </span>
                </li>
              </ul>
            </div>
          </div> */}
        </div>
      </nav>
      {/* Mobile Tab Switcher — only visible on mobile */}
      <div className="pricing-mobile-tabs">
        <button
          className={`pricing-tab-btn${mobileTab === "individual" ? " active individual-active" : ""}`}
          onClick={() => setMobileTab("individual")}
        >
          Plan for Individual
        </button>
        <button
          className={`pricing-tab-btn${mobileTab === "school" ? " active school-active" : ""}`}
          onClick={() => setMobileTab("school")}
        >
          Complete Module for Schools
        </button>
      </div>
      {/* Offer timer banner commented out for web and mobile views
      <OfferTimerBanner createdAt={userData?.created} />
      */}
      {/* Main Content */}
      {/* {JSON.stringify(userData?.admin_id)} */}
      <div className="pricing-main-content">
        {/* <div
          className="pricing-bg-overlay"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/pricing-page-bg.jpeg)`,
          }}
        /> */}
        <div className="pricing-container">
          {/* Header */}
          {/* <div className="pricing-header">
            <h1>
              Choose Your Plan
            </h1>
            <p>
              All plans include 12 months of access.
            </p>
          </div> */}

          {/* When 35% deal is applied — show a Back button instead of the offers */}
          {dealDiscountApplied && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: "10px",
                margin: "12px 0",
              }}
            >
              <button
                type="button"
                onClick={() => setDealDiscountApplied(false)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#fff",
                  color: "#0b3a83",
                  border: "1.5px solid #0b3a83",
                  borderRadius: "999px",
                  padding: "8px 16px",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(11,58,131,0.12)",
                }}
              >
                <span style={{ fontSize: "16px", lineHeight: 1 }}>←</span> Back to Offers
              </button>
              <span style={{ color: "#0f9b47", fontWeight: 600, fontSize: "13px" }}>
                ✓ {DEAL_DISCOUNT_PCT}% Special Offer Discount Applied
              </span>
            </div>
          )}

          {/* Exclusive Deal Banner — only within 89 minutes of user registration; hidden once 35% deal is applied */}
          {isDealEligible && !dealDiscountApplied && (
            <div className="exclusive-deal-section">
              <div className="deal-subtitle-row">
                <div className="deal-header-left">
                  <h2 className="deal-headline">
                    Welcome Offers - <span className="deal-highlight">Only for New Registrations</span>
                  </h2>
                  <div className="deal-subtitle">
                    <span className="deal-subtitle-hi">सीमित समय के लिए</span>
                    <span className="deal-subtitle-sep">|</span>
                    <span className="deal-subtitle-en">Limited Time Offer</span>
                  </div>
                </div>

                <div className="deal-timer-box">
                  <span className="deal-timer-label">
                    <i className="fa fa-clock-o" aria-hidden="true"></i> Offer Ends In
                  </span>
                  <div className="deal-timer-units">
                    <div className="deal-timer-unit">
                      <span className="deal-timer-num">{padTime(dealTimer.hrs)}</span>
                      <span className="deal-timer-cap">HRS</span>
                    </div>
                    <span className="deal-timer-colon">:</span>
                    <div className="deal-timer-unit">
                      <span className="deal-timer-num">{padTime(dealTimer.mins)}</span>
                      <span className="deal-timer-cap">MINS</span>
                    </div>
                    <span className="deal-timer-colon">:</span>
                    <div className="deal-timer-unit">
                      <span className="deal-timer-num">{padTime(dealTimer.secs)}</span>
                      <span className="deal-timer-cap">SECS</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="deal-cards-row">
                <div className={`deal-card deal-card-individual${mobileTab !== "individual" ? " mobile-hidden" : ""}`}>
                  <div className="deal-card-top-bar deal-bar-green"></div>
                  <div className="deal-card-body">
                    <div className="deal-card-head">
                      <div className="deal-card-titles">
                        <div className="deal-card-title">
                          {`Pay ₹${specialPrice} Only & Get Full Access to Total ${allFeatureIds.length} Features`}
                          <span className="deal-card-feature-pill deal-validity-inline">
                            {`With ${specialPlan?.validity_months ?? 3} Months Validity`}
                          </span>
                        </div>
                      </div>
                      <div className="deal-card-priceblock">
                        {/* <div className="deal-card-prices">
                          <span className="deal-price-now">₹{specialPrice}</span>
                          {specialPlan?.actual_price && (
                            <span className="deal-price-old">₹{Number(specialPlan.actual_price)}</span>
                          )}
                        </div> */}
                        {specialPlan?.actual_price && Number(specialPlan.actual_price) > specialPrice && (
                          <span className="deal-savings-pill">Save ₹{Number(specialPlan.actual_price) - specialPrice}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="deal-cta-btn deal-cta-green"
                      onClick={openIndividualPopup}
                    >
                      Claim This Offer <span className="deal-cta-arrow">→</span>
                    </button>
                  </div>
                </div>

                <div className="deal-card deal-card-school">
                  <div className="deal-card-top-bar deal-bar-green"></div>
                  <div className="deal-card-body">
                    <div className="deal-card-head">
                      <div className="deal-card-titles">
                        <div className="deal-card-title">
                          Choose Any Plan & Features + Get 35% Flat Discount
                          <span className="deal-card-feature-pill deal-validity-inline">With 12 Months Validity</span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="deal-cta-btn deal-cta-green"
                      onClick={() => {
                        if (dealDiscountApplied) {
                          // alert(`${DEAL_DISCOUNT_PCT}% discount is already applied — enjoy now!`);
                          return;
                        }
                        setDealDiscountApplied(true);
                        // alert(`${DEAL_DISCOUNT_PCT}% discount applied — you can enjoy now!`);
                      }}
                    >
                      {dealDiscountApplied
                        ? `${DEAL_DISCOUNT_PCT}% Discount Applied ✓`
                        : (<>Claim This Offer <span className="deal-cta-arrow">→</span></>)}
                    </button>
                  </div>
                </div>
              </div>

              <div className="deal-secure-row">
                <span className="deal-secure-lock">🔒</span>
                <span className="deal-secure-text">Secure Payment</span>
                <span className="deal-secure-dot">·</span>
                <span className="deal-secure-text-muted">Valid for new registrations only</span>
                <span className="deal-secure-dot deal-secure-hindi-sep">·</span>
                <span className="deal-secure-text-muted deal-secure-hindi">नए पंजीकृत उपयोगकर्ताओं के लिए</span>
              </div>
            </div>
          )}

          {/* Two Columns Layout */}
          <div className="pricing-columns">
            {/* Column 2: Customized Plan for Individual Users */}
            <div
              className={`pricing-column individual-column${mobileTab !== "individual" ? " mobile-hidden" : ""}`}
            >
              <div className="column-header">
                <div className="header-content-wrapper">
                  <div className="header-text-section">
                    <h2>
                      Plan for Individual Teachers &nbsp;
                      <span className="header-price-inline individual">
                        Starts From ₹250/user
                      </span>
                    </h2>
                  </div>
                </div>
              </div>
              <div className="school-features-header">
                <span>Choose Your Features &nbsp;–&nbsp; Validity 1 Year</span>
              </div>

              {/* Vertical Landing-page style module cards — Documents always pre-checked */}
              <div className="ind-vertical-cards">
                {allModules.map((module) => {
                  const isSelected = selectedModules.includes(module.id);
                  const price = module.id === 6 ? "₹0" : "₹250";
                  return (
                    <div
                      key={module.id}
                      className={`ind-horiz-card ${isSelected ? "selected" : ""} ${module.locked ? "locked" : ""}`}
                      style={{ background: module.gradient }}
                      onClick={() => handleModuleToggle(module.id)}
                    >
                      <div className="ind-horiz-icon">
                        <img
                          src={module.icon}
                          alt={module.name}
                          onError={(e) =>
                            (e.currentTarget.style.display = "none")
                          }
                        />
                      </div>
                      <div className="ind-horiz-text">
                        <span className="ind-horiz-name">{module.name}</span>
                        <span className="ind-horiz-subname">
                          {module.subName}
                        </span>
                      </div>
                      <div className="ind-horiz-right">
                        <span className="ind-horiz-price">
                          {price}
                          <span className="ind-horiz-per">/user</span>
                        </span>
                        <span
                          className={`school-module-badge badge-${module.badgeVariant}`}
                        >
                          {module.badge}
                        </span>
                      </div>
                      <div
                        className={`ind-horiz-check ${isSelected ? "checked" : ""}`}
                      >
                        {isSelected && "✓"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Money-back guarantee bar */}
              {/* <div className="school-teacher-highlight-individual">
                <span className="school-teacher-star-individual">★</span>
                <span className="school-teacher-highlight-text-individual">Buy 4 Features, Risk‑Free — 15‑Days Guarantee!</span>
              </div> */}

              {/* Summary + payment — shown once at least 1 non-doc module selected */}
              {selectedCustomPlan && (
                <div className="ind-summary">
                  {/* Number of Users */}
                  <div className="users-section">
                    <label>Number of Users:</label>
                    <div className="users-input">
                      <button
                        onClick={() =>
                          setNumberOfUsers(Math.max(1, numberOfUsers - 1))
                        }
                        disabled={customAppliedCoupon !== null}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        value={numberOfUsers}
                        onChange={(e) =>
                          setNumberOfUsers(
                            Math.max(1, parseInt(e.target.value) || 1),
                          )
                        }
                        min="1"
                        disabled={customAppliedCoupon !== null}
                      />
                      <button
                        onClick={() => setNumberOfUsers(numberOfUsers + 1)}
                        disabled={customAppliedCoupon !== null}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Coupon */}
                  <div className="coupon-row">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={customCouponCode}
                      onChange={(e) => setCustomCouponCode(e.target.value)}
                      disabled={customAppliedCoupon !== null}
                    />
                    {customAppliedCoupon ? (
                      <button
                        className="coupon-btn remove"
                        onClick={handleCustomRemoveCoupon}
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        className="coupon-btn apply"
                        onClick={handleCustomApplyCoupon}
                        disabled={isValidatingCoupon}
                      >
                        {isValidatingCoupon ? "..." : "Apply"}
                      </button>
                    )}
                  </div>
                  {customAppliedCoupon && (
                    <div className="coupon-success">
                      Coupon "{customAppliedCoupon.code}" applied (
                      {typeof customAppliedCoupon.discount === "string"
                        ? customAppliedCoupon.discount
                        : `₹${customAppliedCoupon.discount}`}{" "}
                      off)
                    </div>
                  )}

                  {/* Price Breakdown */}
                  <div className="price-breakdown">
                    <div className="breakdown-row">
                      <span>Price per user</span>
                      <span>₹{customizedBasePricePerUser}</span>
                    </div>
                    <div className="breakdown-row">
                      <span>Number of users</span>
                      <span>× {numberOfUsers}</span>
                    </div>
                    {dealDiscountApplied && (
                      <div className="breakdown-row">
                        <span style={{ color: "#0f9b47", fontWeight: 600 }}>
                          Special Offer Discount
                        </span>
                        <span style={{ color: "#0f9b47", fontWeight: 600 }}>
                          -{DEAL_DISCOUNT_PCT}%
                        </span>
                      </div>
                    )}
                    <div className="breakdown-row subtotal">
                      <span>Subtotal</span>
                      <span>
                        ₹{customizedPriceAfterDiscount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  {dealDiscountApplied && (
                    <div
                      style={{
                        background: "#e8f7ee",
                        color: "#0f9b47",
                        border: "1px solid #b8e5c8",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "13px",
                        fontWeight: 600,
                        textAlign: "center",
                        margin: "8px 0",
                      }}
                    >
                      ✓ {DEAL_DISCOUNT_PCT}% Special Offer Discount Applied
                    </div>
                  )}

                  <div className="total-row">
                    <span>Total Amount:</span>
                    <span className="total-price">
                      ₹{customizedFinalPrice.toLocaleString()}
                    </span>
                  </div>

                  <button
                    className={`pay-button individual ${!isCustomizedValid ? "disabled" : ""}`}
                    onClick={handleCustomizedPayment}
                    disabled={!isCustomizedValid}
                  >
                    Pay Now ₹{customizedFinalPrice.toLocaleString()}
                  </button>
                </div>
              )}
            </div>
            {/* Column 1: Complete Modules for School */}
            <div
              className={`pricing-column school-column${mobileTab !== "school" ? " mobile-hidden" : ""}`}
            >
              <div className="column-header">
                <div className="header-content-wrapper">
                  <div className="header-text-section">
                    <h2>
                      All in One Plan for Schools (6+ Features) &nbsp;
                      <span className="header-price-inline"></span>
                    </h2>
                  </div>
                </div>
              </div>

              {/* User Count Selection */}
              <div className="school-plans-section">
                <div className="choose-users-label">Choose Your Plan:</div>
                <div className="users-count-row">
                  {completePlans.slice(0, 3).map((plan) => {
                    const isSelected =
                      selectedCompletePlan?.model_id === plan.model_id;
                    const theme =
                      schoolPlanThemes[plan.total_users] ||
                      defaultSchoolPlanTheme;

                    return (
                      <div
                        key={plan.model_id}
                        className={`user-count-card user-count-card-colorful ${isSelected ? "selected" : ""}`}
                        style={{
                          background: isSelected
                            ? theme.selectedGradient
                            : theme.gradient,
                          borderColor: theme.borderColor,
                          boxShadow: isSelected
                            ? theme.selectedShadow
                            : theme.shadow,
                        }}
                        onClick={() => {
                          if (
                            selectedCompletePlan?.model_id === plan.model_id
                          ) {
                            setSelectedCompletePlan(null);
                          } else {
                            setSelectedCompletePlan(plan);
                            setSelectedModules([6]);
                            // Keep customCouponCode so the WELCOME20 auto-fill
                            // remains visible when user switches back to the individual side.
                            setCustomAppliedCoupon(null);
                            setCustomCouponId(null);
                          }
                        }}
                      >
                        <div className="user-count-top">
                          <div className="user-count-number">
                            {plan.total_users} users
                          </div>
                          <div className="user-count-price">
                            ₹{Number(plan.price).toLocaleString()} /-
                          </div>
                        </div>
                        {/* <div className="user-count-subtext">(1 Admin + {plan.total_users - 1} users)</div> */}
                      </div>
                    );
                  })}
                  <a
                    href="https://wa.me/919289440049?text=Hi%2C%20I%27m%20interested%20in%20a%20customised%20plan%20for%20100%2B%20users.%20Please%20share%20more%20details."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="user-count-card user-count-card-colorful user-count-card-custom"
                    style={{
                      background: schoolPlanThemes[120].gradient,
                      borderColor: schoolPlanThemes[120].borderColor,
                      boxShadow: schoolPlanThemes[120].shadow,
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    <div className="user-count-top">
                      <div className="user-count-number">100+ users</div>
                    </div>
                    <div className="user-count-subtext">
                      Customised plans for 100+ users
                    </div>
                  </a>
                </div>

                {/* Coupon & Payment - shown when plan selected */}
                {selectedCompletePlan && (
                  <div className="school-footer">
                    <div className="coupon-row">
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={completeCouponCode}
                        onChange={(e) => setCompleteCouponCode(e.target.value)}
                        disabled={completeAppliedCoupon !== null}
                      />
                      {completeAppliedCoupon ? (
                        <button
                          className="coupon-btn remove"
                          onClick={handleCompleteRemoveCoupon}
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          className="coupon-btn apply"
                          onClick={handleCompleteApplyCoupon}
                          disabled={isValidatingCoupon}
                        >
                          {isValidatingCoupon ? "..." : "Apply"}
                        </button>
                      )}
                    </div>
                    {completeAppliedCoupon && (
                      <div className="coupon-success">
                        Coupon "{completeAppliedCoupon.code}" applied (
                        {typeof completeAppliedCoupon.discount === "string"
                          ? completeAppliedCoupon.discount
                          : `₹${completeAppliedCoupon.discount}`}{" "}
                        off)
                      </div>
                    )}
                    {dealDiscountApplied && selectedCompletePlan && (
                      <>
                        <div
                          style={{
                            background: "#e8f7ee",
                            color: "#0f9b47",
                            border: "1px solid #b8e5c8",
                            borderRadius: "8px",
                            padding: "8px 12px",
                            fontSize: "13px",
                            fontWeight: 600,
                            textAlign: "center",
                            margin: "8px 0",
                          }}
                        >
                          ✓ {DEAL_DISCOUNT_PCT}% Special Offer Discount Applied
                        </div>
                        <div className="breakdown-row" style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                          <span style={{ textDecoration: "line-through", color: "#888" }}>
                            ₹{Number(selectedCompletePlan.price).toLocaleString()}
                          </span>
                          <span style={{ color: "#0f9b47", fontWeight: 600 }}>
                            -{DEAL_DISCOUNT_PCT}%
                          </span>
                        </div>
                      </>
                    )}
                    <div className="total-row">
                      <span>Total Amount:</span>
                      <span className="total-price">
                        ₹{completePlanFinalPrice.toLocaleString()}
                      </span>
                    </div>
                    <button
                      className="pay-button school"
                      onClick={handleCompletePayment}
                    >
                      PAY NOW
                    </button>
                  </div>
                )}
              </div>
              {/* end school-plans-section */}

              {/* Module Features */}
              <div className="school-features-section">
                <div className="school-features-header">
                  {/* <span>Choose Your Features &nbsp;–&nbsp; Validity 1 Year</span> */}
                  <span>
                    Features Included in School Plan&nbsp;–&nbsp; Validity 1
                    Year
                  </span>
                </div>
                <div className="school-features-text-list">
                  {schoolModulesList.map((module, idx) => {
                    const isQP = module.dynamic;

                    return (
                      <div key={idx} className="school-feature-text-item">
                        <span className="school-feature-check">✓</span>

                        {isQP ? (
                          <span className="feature-text-with-pill">
                            Create Question Paper
                            {/* ✅ Show only when plan selected */}
                            {selectedCompletePlan && (
                              <span className="papers-pill">
                                {paperCount} papers
                              </span>
                            )}
                          </span>
                        ) : (
                          <span>{module.name}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="school-teacher-highlight-individual">
                  <span className="school-teacher-highlight-text-individual">
                    Every School Plan gives all teachers full access to every
                    feature — at less than ₹500 per teacher.
                  </span>
                </div>
                {/* <div className="school-teacher-highlight-individual">
                  <span className="school-teacher-star-individual">★</span>
                  <span className="school-teacher-highlight-text-individual">Buy any plan &amp; get 30‑Days Guarantee! (No questions asked)</span>
                </div> */}
                <div
                  className="csr-support-card"
                  onClick={async () => {
                    if (userData?.admin_id) {
                      const response = await dispatch(
                        getStatusAPI(userData?.admin_id),
                      );
                      if (
                        response?.type === "ticketing/getStatusAPI/fulfilled"
                      ) {
                        if (!response?.payload?.csr_filled) {
                          setShowPopUp(true);
                        }
                        if (response?.payload?.csr_filled) {
                          alert("CSR form is already filled");
                        }
                      }
                      console.log("resss", JSON.stringify(response));
                    } else {
                      alert("Please register to fill the form");
                      navigate("/registration");
                    }
                  }}
                >
                  <div className="csr-icon">
                    <i className="fa fa-handshake-o"></i>
                  </div>

                  <div className="csr-content">
                    <div className="csr-title">
                      School Plans can also be applied with CSR Support & Grants
                      — where School Pays 30% & CSR Organisation 70%
                    </div>

                    <div className="csr-subtitle">
                      स्कूल प्लान CSR अनुदान योजना में भी लिए जा सकते हैं जिसमें
                      स्कूल को 30% तक देना होगा और CSR देने वाली कंपनी या संस्था
                      70% तक देगी
                    </div>
                  </div>

                  <div className="csr-arrow">
                    <i className="fa fa-angle-right"></i>
                  </div>
                </div>
              </div>
              {/* end school-features-section */}
            </div>
          </div>
        </div>
        {/* Skip Payment button — visible only within 15 minutes of registration */}
        {!skipPaymentExpired && (
          <div className="skip-payment-bar">
            <button className="skip-payment-btn" onClick={() => navigate("/")}>
              Skip Payment — Enjoy Limited Access For Free
            </button>
          </div>
        )}
      </div>

      {/* Special offer popup — opened from "Grab This Deal" on the individual card */}
      {openModal && (
        <div
          className="deal-modal-overlay"
          onClick={() => (openModal === "individual" ? closePopupAndReset() : setOpenModal(null))}
          role="dialog"
          aria-modal="true"
        >
          <div
            className={`deal-modal-container ${openModal === "individual" ? "deal-modal-individual" : "deal-modal-school"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="deal-modal-close"
              onClick={() => (openModal === "individual" ? closePopupAndReset() : setOpenModal(null))}
              aria-label="Close"
            >
              ×
            </button>
            <div className="deal-modal-body">
              {openModal === "individual" && (
                <div className="pricing-column individual-column deal-modal-column">
                  <div className="column-header">
                    <div className="header-content-wrapper">
                      <div className="header-text-section">
                        <h2>
                          Plan for Individuals
                          <span style={{ fontWeight: 700, color: "#383838" }}> - Complete {allFeatureIds.length} Features Unlocked</span>
                        </h2>
                      </div>
                    </div>
                  </div>
                  <div className="school-features-header">
                    <span>{`Validity ${specialPlan?.validity_months ?? specialPlan?.validity ?? 3} Months`}</span>
                  </div>

                  <div className="ind-vertical-cards">
                    {[
                      ...allModules,
                      {
                        id: 6,
                        name: "Documents & Correspondences",
                        subName: "दस्तावेज़ और पत्राचार",
                        gradient: "linear-gradient(135deg, #018bd6 0%, #33aaee 100%)",
                        icon: "/images/documents.png",
                        badge: "UNLIMITED",
                        badgeVariant: "unlimited",
                      },
                    ].map((module) => {
                      const isSelected = selectedModules.includes(module.id);
                      return (
                        <div
                          key={module.id}
                          className={`ind-horiz-card ${isSelected ? "selected" : ""} locked`}
                          style={{ background: module.gradient, cursor: "not-allowed", pointerEvents: "none" }}
                          onClick={() => handlePopupModuleToggle(module.id)}
                        >
                          <div className="ind-horiz-icon">
                            <img
                              src={module.icon}
                              alt={module.name}
                              onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                          </div>
                          <div className="ind-horiz-text">
                            <span className="ind-horiz-name">{module.name}</span>
                            <span className="ind-horiz-subname">{module.subName}</span>
                          </div>
                          <div className="ind-horiz-right">
                            {/* <span className="ind-horiz-price">
                              {module.id === 6 ? "₹0" : "₹250"}
                              <span className="ind-horiz-per">/user</span>
                            </span> */}
                            <span className={`school-module-badge badge-${module.badgeVariant}`}>
                              {module.badge}
                            </span>
                          </div>
                          <div className={`ind-horiz-check ${isSelected ? "checked" : ""}`}>
                            {isSelected && "✓"}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="ind-summary">
                    {/* Coupon code intentionally hidden for this special offer
                    <div className="coupon-row">
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={customCouponCode}
                        onChange={(e) => setCustomCouponCode(e.target.value)}
                        disabled={customAppliedCoupon !== null}
                      />
                      {customAppliedCoupon ? (
                        <button className="coupon-btn remove" onClick={handleCustomRemoveCoupon}>Remove</button>
                      ) : (
                        <button className="coupon-btn apply" onClick={handleCustomApplyCoupon} disabled={isValidatingCoupon}>
                          {isValidatingCoupon ? "..." : "Apply"}
                        </button>
                      )}
                    </div>
                    */}

                    <div className="price-breakdown">
                      <div className="breakdown-row subtotal">
                        <span>Price</span>
                        <span>₹{specialPrice}</span>
                      </div>
                    </div>

                    <div className="total-row">
                      <span>Total Amount:</span>
                      <span className="total-price">
                        ₹{specialPrice.toLocaleString()}
                      </span>
                    </div>

                    <button
                      className={`pay-button individual ${!popupHasDeal ? "disabled" : ""}`}
                      onClick={handlePopupPayment}
                      disabled={!popupHasDeal}
                      title={!popupHasDeal ? "This deal is not available right now" : ""}
                    >
                      {popupHasDeal
                        ? `Pay Now ₹${specialPrice.toLocaleString()}`
                        : "Deal unavailable"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />

      {/* Payment loader popup — shown while initiating payment (page redirects on success) */}
      {paymentLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Processing"
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "14px",
              padding: "28px 36px",
              minWidth: "240px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "14px",
            }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                border: "4px solid #e5e7eb",
                borderTopColor: "#0b3a83",
                borderRadius: "50%",
                animation: "pricing-payment-spin 0.9s linear infinite",
              }}
            />
            <div style={{ fontSize: "15px", fontWeight: 600, color: "#0b3a83" }}>
              Processing....
            </div>
            <style>
              {`@keyframes pricing-payment-spin { to { transform: rotate(360deg); } }`}
            </style>
          </div>
        </div>
      )}

      {showPopUp && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px", // breathing room on small screens
          }}
          onClick={() => setShowPopUp(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: 0, // ✅ let SchoolGrantForm control its own padding
              maxWidth: "900px", // ✅ wide enough for the form (was 400px)
              width: "95%", // ✅ fixed typo (was "1090%")
              maxHeight: "90vh", // ✅ don't overflow the screen
              overflowY: "auto", // ✅ scroll inside popup if form is tall
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <CSRSchoolPlanForm />
          </div>
        </div>
      )}
    </div>
  );
}

export default PricingPage;
